<template>
  <div class="add-product">
    <KTCodePreview v-bind:title="''">
      <template v-slot:preview>
        <v-form ref="form" v-model="valid" lazy-validation>
          <b-card no-body class="full-width">
            <!-- Loading Overlay -->
            <b-overlay :show="loading" rounded="sm" opacity="0.6">
              <!-- Wizard Structure -->
              <product-wizard
                :current-step="currentStep"
                :show-combo="isComboProduct"
                :action="'add'"
                @step-changed="handleStepChange"
              >
                <!-- Step 1 Content -->
                <template v-slot:step-1>
                  <div class="col-md-12 full-width">
                    <product-information-section
                      v-model="productInfo"
                      :validation="$v.form"
                      :units="unitOptions"
                      :statuses="listStatus"
                      :categories="categories"
                      :filtered-categories="filteredCategories"
                      :brands="brands"
                      :filtered-brands="filteredBrands"
                      :product-types="listType"
                      :validation-state="validateState"
                      :filtered-options="filteredProducts"
                      @category-selected="handleCategorySelect"
                      @category-changed="handleCategoryChange"
                      @brand-selected="handleBrandSelect"
                      @brand-changed="handleBrandChange"
                      @parent-selected="handleParentSelect"
                      @parent-changed="handleParentChange"
                      @type-selected="handleTypeChange"
                      @generate-child="generateChildProducts"
                      @add-brand="showBrandModalFn"
                      @input-change="handleInputChange"
                    />

                    <!-- Product Options -->
                    <product-options-section
                      :options="productOptions"
                      @add-option="addNewOption"
                      @update:options="updateProductOptions"
                      ref="productOptions"
                    />

                    <!-- Child Products Toggle -->
                    <div
                      style="
                        font-size: 14px;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                      "
                      v-if="
                        !productInfo?.selectedParent &&
                        productInfo?.selectedType !== 3
                      "
                    >
                      <b-form-checkbox
                        v-model="isGenChild"
                        @change="generateChildProducts"
                        name="check-button"
                        switch
                      >
                        Tạo sản phẩm con
                      </b-form-checkbox>
                      <b-button
                        variant="outline-secondary"
                        @click="generateChildProducts"
                        style="
                          margin-left: 10px;
                          padding: 5px 10px;
                          font-size: 12px;
                        "
                      >
                        <b-icon icon="arrow-clockwise"></b-icon>
                      </b-button>
                    </div>

                    <div class="separator separator-dashed my-5"></div>

                    <!-- Child Products Table -->
                    <child-products-table
                      v-if="
                        isGenChild &&
                        !productInfo.selectedParent &&
                        productInfo.selectedType !== 3
                      "
                      :products="childProducts"
                      :fields="fields"
                      @add-product="showAddProductModalFn"
                      @edit-product="handleEditChildProduct"
                      @delete-product="handleDeleteChildProduct"
                      :check-permission="checkPermission"
                    />
                  </div>
                </template>

                <!-- Step 2 Content -->
                <template v-slot:step-2>
                  <product-combo-section
                    :active="currentStep === 2"
                    :products="comboProducts"
                    :fields="fieldTableCombo"
                    @add-product="handleAddComboProductClick"
                    @edit-product="handleEditComboProduct"
                    @update-product="handleUpdateComboProduct"
                    @delete-product="handleDeleteComboProduct"
                    :check-permission="checkPermission"
                  />
                </template>
              </product-wizard>
            </b-overlay>

            <!-- Modals -->
            <brand-modal
              :value="showBrandModal"
              :brandName.sync="brandName"
              @save="createBrand"
            />

            <child-product-modal
              ref="add-product-modal"
              v-model="showAddProduct"
              :product-options="productOptions"
              :initial-selected-options="selectedOptionValues"
              @option-selected="handleOptionSelected"
              :barcode.sync="childBarCode"
              :original-price.sync="childOriginalPrice"
              :sale-price.sync="childSalePrice"
              @save="createChildProduct"
              @cancel="hideAddProductModal"
            />

            <update-product-modal
              ref="update-product-modal"
              :barcode.sync="childBarCode"
              :code.sync="childCode"
              :name.sync="childName"
              :original-price.sync="childOriginalPrice"
              :sale-price.sync="childSalePrice"
              @save="updateChildProduct"
              @cancel="hideUpdateProductModal"
            />

            <product-combo-modal
              ref="add-product-combo-modal"
              :search-product.sync="searchProductForCombo"
              :quantity.sync="proComboQuantity"
              :combo-price.sync="productComboPrice"
              :combo-rate.sync="productComboRate"
              :filtered-options="filteredProducts"
              @select="handleComboProductSelect"
              @change="handleComboProductSearchChange"
              @save="addProductToCombo"
              @cancel="hideComboProductModal"
            />
          </b-card>
        </v-form>
      </template>

      <template v-slot:foot>
        <div class="col-md-12 full-width">
          <div class="row">
            <div class="col-md-3">
              <span
                v-b-tooltip.hover
                :title="isCreateProductBlocked ? createProductBlockedTooltip : ''"
                class="d-inline-block"
              >
                <b-button
                  style="fontweight: 600; width: 70px"
                  variant="primary"
                  size="sm"
                  @click="debounceCreateProduct"
                  :disabled="loading || isCreateProductBlocked"
                  >Lưu</b-button
                >
              </span>
              <router-link to="/products" tag="button">
                <b-button
                  style="margin-left: 10px; font-weight: 600; width: 70px"
                  variant="secondary"
                  size="sm"
                  >Hủy</b-button
                >
              </router-link>
            </div>
          </div>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { validationMixin } from 'vuelidate';
import debounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';
import { getToastInstance } from '@/utils/toastHelper';
import { unMaskPrice } from '@/utils/common';
import localData from '@/utils/saveDataToLocal';
import KTCodePreview from '@/view/content/CodeTab.vue';
import KTUtil from '@/assets/js/components/util';
import KTWizard from '@/assets/js/components/wizard';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  UNIT_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  PRODUCT_STATUS_OPTIONS,
  PRODUCT_HEADER_FIELDS,
  COMBO_HEADER_FIELDS,
} from '@/utils/types';

// Components imports
import ProductWizard from './components/ProductWizard.vue';
import ProductInformationSection from './components/ProductInformationSection.vue';
import ProductOptionsSection from './components/ProductOptionsSection.vue';
import ChildProductsTable from './components/ChildProductsTable.vue';
import ProductComboSection from './components/ProductComboSection.vue';
import BrandModal from './components/modals/BrandModal.vue';
import ChildProductModal from './components/modals/ChildProductModal.vue';
import UpdateProductModal from './components/modals/UpdateProductModal.vue';
import ProductComboModal from './components/modals/ProductComboModal.vue';

// Import service, actions and validators
import { ProductActions } from './productActions';
import { productValidators } from './validators/productValidators';

// Import store module and mutation helpers
import productModule from '@/core/services/store/modules/product.module';
import { PRODUCT_MUTATIONS } from '@/core/services/store/modules/mutationTypes';
import { createMutationCommitters } from '@/core/services/store/modules/mutationHelpers';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { PRODUCT_TYPE } from '@/utils/enum';
import companyCatalogFlagsMixin from '@/view/mixins/companyCatalogFlags.mixin';

export default {
  name: 'AddProduct',
  mixins: [validationMixin, companyCatalogFlagsMixin],
  components: {
    KTCodePreview,
    ProductWizard,
    ProductInformationSection,
    ProductOptionsSection,
    ChildProductsTable,
    ProductComboSection,
    BrandModal,
    ChildProductModal,
    UpdateProductModal,
    ProductComboModal,
  },
  validations: {
    form: {
      code: {
        required: (val) => !!val,
        maxLength: (val) => !val || val.length <= 50,
        minLength: (val) => !val || val.length >= 3,
        hasSpecialCharacters: productValidators.hasSpecialCharacters,
        checkAscent: productValidators.checkAscent,
      },
      name: {
        required: (val) => !!val,
      },
      selectedCate: {
        required: (val) => !!val,
      },
      // listedPrice: {
      //   required: (val) => !!val,
      //   checkCurrency: productValidators.checkCurrency,
      // },
      unitId: {
        required(val) {
          return this.isUnitRequired ? !!val : true;
        },
      },
      productVat: {
        required(val) {
          return this.isVatRequired ? val !== null && val !== undefined : true;
        },
      },
    },
    color: {
      colorCode: {
        required: (val) => !!val,
      },
      colorName: {
        required: (val) => !!val,
      },
    },
    status: {
      statusCode: {
        required: (val) => !!val,
      },
      statusName: {
        required: (val) => !!val,
      },
    },
  },
  data() {
    return {
      productImages: [],

      currentStep: 1,
      valid: true,
      isGenChild: false,
      showBrandModal: false,
      showStatusModal: false,
      selectedOptionValues: {},
      showAddProduct: false,

      // Form data
      form: {
        name: '',
        code: '',
        category: '',
        listedPrice: 0,
        selectedCate: '',
        searchBrandName: '',
        unitId: null,
        productVat: null,
      },

      // Modal data
      brandName: '',
      childBarCode: '',
      childOriginalPrice: 0,
      childSalePrice: 0,
      childCode: '',
      childName: '',
      childId: 0,
      selectedColorChild: null,
      selectedSizeChild: null,
      searchProductForCombo: '',
      proComboQuantity: 1,
      productComboPrice: '',
      productComboRate: '',

      // Table configs
      fields: PRODUCT_HEADER_FIELDS,
      fieldTableCombo: COMBO_HEADER_FIELDS,

      // Static options
      listStatus: PRODUCT_STATUS_OPTIONS,
      listType: PRODUCT_TYPE_OPTIONS,
      unitOptions: UNIT_OPTIONS,

      // Property IDs
      productPropertyId: null,

      // Selected combo product
      selectedProductForCombo: {
        id: null,
        code: '',
        name: '',
        quantity: 0,
      },
    };
  },
  computed: {
    // Map store state
    ...mapState({
      loading: (state) => state.product.loading,
      error: (state) => state.product?.error,
      categories: (state) => state.product.categories,
      filteredCategories: (state) => state.product.filteredCategories,
      brands: (state) => state.product.brands,
      filteredBrands: (state) => state.product.filteredBrands,
      colors: (state) => state.product.colors,
      sizes: (state) => state.product.sizes,
      productOptions: (state) => state.product.productOptions,
      childProducts: (state) => state.product.childProducts,
      comboProducts: (state) => state.product.comboProducts,
      filteredProducts: (state) => state.product.filteredProducts,
      serverErrors: (state) => state.product?.error,
    }),

    productInfo: {
      get() {
        return this.$store.getters['product/getProductInfo'] || {};
      },
      set(value) {
        this.$store.commit('product/UPDATE_PRODUCT_INFO', value);
      },
    },

    isComboProduct() {
      return this.$store.getters['product/getShowCombo'];
    },
    isUnitRequired() {
      return this.productInfo?.selectedType !== PRODUCT_TYPE.PRODUCT_SERVICE;
    },
    isVatRequired() {
      return this.productInfo?.selectedType !== PRODUCT_TYPE.PRODUCT_SERVICE;
    },
  },
  created() {
    if (!this.$store.state.product) {
      this.$store.registerModule('product', productModule);
    }

    this.resetProductInfo();
    this.setProductOptions([]);

    this.initData();
    this.mutationCommitters = createMutationCommitters(this.$store.commit);
  },
  mounted() {
    this.setupBreadcrumbs();
    this.initializeWizard();
    this.syncFormWithProductInfo();
  },
  watch: {
    productInfo: {
      handler() {
        this.syncFormWithProductInfo();
      },
      deep: true,
    },
    serverErrors(newVal) {
      if (newVal) {
        makeToastFaile(newVal);
      }
    },
  },
  methods: {
    ...getToastInstance(),

    // Map Vuex actions
    ...mapActions({
      initProductData: 'product/initProductData',
      updateProductInfo: 'product/updateProductInfo',
      setProductOptions: 'product/setProductOptions',
      generateChildProductsAction: 'product/generateChildProducts',
      addChildProduct: 'product/addChildProduct',
      updateChildProductAction: 'product/updateChildProduct',
      removeChildProduct: 'product/removeChildProduct',
      addComboProduct: 'product/addComboProduct',
      updateComboProduct: 'product/updateComboProduct',
      removeComboProduct: 'product/removeComboProduct',
      toggleComboProductEdit: 'product/toggleComboProductEdit',
      calculateComboSum: 'product/calculateComboSum',
      saveProductAction: 'product/saveProduct',
      filterCategories: 'product/filterCategories',
      filterBrands: 'product/filterBrands',
      createBrandAction: 'product/createBrand',
      createPropertyValue: 'product/createPropertyValue',
      searchProducts: 'product/searchProducts',
      resetProductInfo: 'product/resetProductInfo',
    }),

    handleImageSubmit(imageIds) {
      this.updateProductInfo({ imageIds });
    },

    handleStepChange(step) {
      this.currentStep = step;

      // Đảm bảo wizard được cập nhật
      setTimeout(() => {
        const wizard = document.querySelector('#kt_wizard_v4');
        if (wizard) {
          const steps = wizard.querySelectorAll('[data-wizard-type="step"]');
          steps.forEach((stepEl, index) => {
            if (index + 1 === step) {
              stepEl.setAttribute('data-wizard-state', 'current');
            } else {
              stepEl.removeAttribute('data-wizard-state');
            }
          });
        }
      }, 0);
    },

    handleOptionSelected({ optionId, value }) {
      this.$set(this.selectedOptionValues, optionId, value);
    },

    goToStep(step) {
      if (this.wizard) {
        this.wizard.goTo(step);
      } else {
        this.currentStep = step;
      }
    },

    // Utility functions
    checkPermission(condition) {
      return localData.checkPermission(condition);
    },

    // Initialization methods
    async initData() {
      await ProductActions.initializeProductEditor(this);
    },

    setupBreadcrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Sản phẩm', route: '/products' },
        { title: 'Danh sách sản phẩm', route: '/products' },
        { title: 'Thêm mới sản phẩm' },
      ]);
    },

    initializeWizard() {
      const wizard = new KTWizard('kt_wizard_v4', {
        startStep: 1,
        clickableSteps: true,
      });

      // Lưu wizard vào instance để sử dụng sau này
      this.wizard = wizard;

      wizard.on('change', (wizardObj) => {
        // Cập nhật currentStep khi wizard thay đổi
        this.currentStep = wizardObj.getStep();

        // Scroll to top
        setTimeout(() => {
          KTUtil.scrollTop();
        }, 500);
      });
    },

    syncFormWithProductInfo() {
      if (this.productInfo) {
        this.form.name = this.productInfo.name || '';
        this.form.code = this.productInfo.code || '';
        this.form.listedPrice = this.productInfo.listedPrice || 0;
        this.form.selectedCate = this.productInfo.selectedCate || '';
        this.form.searchBrandName = this.productInfo.searchBrandName || '';
        this.form.unitId = this.productInfo.unitId || null;
        this.form.productVat = this.productInfo.productVat || null;
      }
    },

    // Handle form input changes
    handleInputChange(field, value) {
      this.form[field] = value;
      this.updateProductInfo({ [field]: value });
    },

    // Validation methods
    validateState(name) {
      const field = this.$v.form[name];
      if (!field) return null;
      return field.$dirty ? !field.$error : null;
    },

    validateStatePropColor(name) {
      const field = this.$v.color[name];
      if (!field) return null;
      return field.$dirty ? !field.$error : null;
    },

    validateStatePropStatus(name) {
      const field = this.$v.status[name];
      if (!field) return null;
      return field.$dirty ? !field.$error : null;
    },

    // Use ProductActions for logic with mutations
    handleCategorySelect(event) {
      const categoryId = event.item.id;
      const categoryName = event.item.name;

      this.form.selectedCate = categoryName;
      this.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_SELECTED_CATEGORY_ID}`,
        categoryId,
      );
      this.updateProductInfo({ selectedCate: categoryName });
    },

    handleCategoryChange(text) {
      this.form.selectedCate = text;
      this.updateProductInfo({ selectedCate: text });

      if (text === '') {
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_FILTERED_CATEGORIES}`,
          [...this.categories],
        );
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_SELECTED_CATEGORY_ID}`,
          null,
        );
      } else {
        this.filterCategories(text);
      }
    },

    handleBrandSelect(event) {
      const brandId = event.item.id;
      const brandName = event.item.name;

      this.form.searchBrandName = brandName;
      this.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_SELECTED_BRAND_ID}`,
        brandId,
      );
      this.updateProductInfo({ searchBrandName: brandName });
    },

    handleBrandChange(text) {
      this.form.searchBrandName = text;
      this.updateProductInfo({ searchBrandName: text });

      if (text === '') {
        this.$store.commit(`product/${PRODUCT_MUTATIONS.SET_FILTERED_BRANDS}`, [
          ...this.brands,
        ]);
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_SELECTED_BRAND_ID}`,
          null,
        );
      } else {
        this.filterBrands(text);
      }
    },

    handleParentSelect(event) {
      const parentId = event.item.id;
      let parentName = event.item.name;

      if (event.item.code !== '') {
        parentName = event.item.code + ' - ' + event.item.name;
      }

      this.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_SELECTED_PARENT_ID}`,
        parentId,
      );
      this.updateProductInfo({ selectedParent: parentName });
      this.generateChildProducts();
    },

    handleParentChange(text) {
      this.updateProductInfo({ selectedParent: text });
      this.searchProducts(text);
      this.generateChildProducts();

      if (!text || text.trim() === '') {
        this.$store.commit(
          `product/${PRODUCT_MUTATIONS.SET_SELECTED_PARENT_ID}`,
          null,
        );
      }
    },

    handleTypeChange() {
      this.generateChildProducts();
    },

    generateChildProducts() {
      this.generateChildProductsAction({ isGenChild: this.isGenChild });
    },

    // Modal methods
    showBrandModalFn() {
      this.showBrandModal = true;
      this.brandName = '';
    },

    async createBrand() {
      if (!this.brandName) {
        return;
      }

      try {
        const result = await this.createBrandAction({
          id: this.$uuid.v4(),
          name: this.brandName,
        });

        this.showBrandModal = false;

        if (result.status === 1) {
          makeToastSuccess(result.message);
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        makeToastFaile('Lỗi khi tạo thương hiệu: ' + error.message);
      }
    },

    async createPropertyStatus(statusName, statusCode) {
      if (!statusName || !statusCode) {
        return;
      }

      try {
        const result = await this.createPropertyValue({
          propertyData: {
            name: statusName,
            code: statusCode,
            productPropertyId: this.productPropertyId,
          },
          typeId: 2, // Status type
        });

        if (result.status === 1) {
          this.showStatusModal = false;
          makeToastSuccess(result.message);
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        makeToastFaile('Lỗi khi tạo thuộc tính: ' + error.message);
      }
    },

    showAddProductModalFn() {
      this.childBarCode = '';
      this.selectedOptionValues = {};
      this.selectedColorChild = null;
      this.selectedSizeChild = null;
      this.childOriginalPrice = 0;
      this.childSalePrice = 0;
      this.showAddProduct = true;
    },

    hideAddProductModal() {
      this.showAddProduct = false;
    },

    handleEditChildProduct(item) {
      this.childId = item.id;
      this.childCode = item.productCode;
      this.childName = item.productName;
      this.childBarCode = item.barCode;
      this.childOriginalPrice = item.originalPrice;
      this.childSalePrice = item.sellingPrice;

      this.$refs['update-product-modal'].show();
    },

    hideUpdateProductModal() {
      this.$refs['update-product-modal'].hide();
    },

    handleAddComboProductClick() {
      this.searchProductForCombo = '';
      this.proComboQuantity = 1;
      this.productComboRate = '';
      this.productComboPrice = '';

      this.$refs['add-product-combo-modal'].show();
    },

    hideComboProductModal() {
      this.$refs['add-product-combo-modal'].hide();
    },

    handleDeleteChildProduct(item) {
      this.showDeleteConfirmation(item, (id) => {
        this.removeChildProduct(id);
      });
    },

    handleDeleteComboProduct(item) {
      this.showDeleteConfirmation(item, (id) => {
        this.removeComboProduct(id);
      });
    },

    showDeleteConfirmation(item, onConfirm) {
      this.$swal
        .fire({
          title: 'Xóa sản phẩm con!',
          text: 'Bạn có chắc muốn xóa sản phẩm con này không ?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'rgb(221, 51, 51)',
          cancelButtonColor: '#E5EAEE',
          confirmButtonText: 'Xóa',
          cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value && onConfirm) {
            onConfirm(item.id);
          }
        });
    },

    handleComboProductSelect(event) {
      this.selectedProductForCombo = {
        id: event.item.id,
        name: event.item.name,
        code: event.item.code,
        quantity: 0,
      };
      this.searchProductForCombo = event.item.name;
    },

    handleComboProductSearchChange(text) {
      this.searchProductForCombo = text;
      this.searchProducts(text);
    },

    handleEditComboProduct(index) {
      this.toggleComboProductEdit(index);
    },

    handleUpdateComboProduct(index) {
      this.toggleComboProductEdit(index);
    },

    createChildProduct(data) {
      const optionValues = Object.values(data.selectedOptionsByName);
      const optionCodes = optionValues.join('-');
      const optionNames = optionValues.join(' - ');

      const product = {
        id: this.form.code + '-' + optionCodes,
        count:
          this.childProducts.length > 0
            ? this.childProducts[this.childProducts.length - 1].count + 1
            : 1,
        productName: this.form.name + (optionNames ? ' - ' + optionNames : ''),
        productCode: this.form.code + '-' + optionCodes,
        barCode: data.barcode,
        originalPrice: data.originalPrice ? unMaskPrice(data.originalPrice) : 0,
        sellingPrice: data.salePrice ? unMaskPrice(data.salePrice) : 0,
        totalQualityInstock: 0,
        options: data.selectedOptionsByName,
        status: this.productInfo.selectedStatus,
      };

      this.addChildProduct(product);
      this.hideAddProductModal();
    },

    updateChildProduct() {
      this.updateChildProductAction({
        id: this.childId,
        updates: {
          productCode: this.childCode,
          productName: this.childName,
          barCode: this.childBarCode,
          originalPrice: this.childOriginalPrice
            ? unMaskPrice(this.childOriginalPrice)
            : 0,
          sellingPrice: this.childSalePrice
            ? unMaskPrice(this.childSalePrice)
            : 0,
        },
      });

      this.hideUpdateProductModal();
    },

    // Combo product methods
    addProductToCombo() {
      const comboProduct = {
        id: this.selectedProductForCombo.id,
        productCode: this.selectedProductForCombo.code,
        productName: this.selectedProductForCombo.name,
        quantity: this.proComboQuantity || 1,
        productComboPrice: this.productComboPrice,
        productComboRate: this.productComboRate,
        isEdit: false,
      };

      // Check for duplicates
      const isDuplicate = this.comboProducts.some(
        (product) => product.id === comboProduct.id,
      );
      if (isDuplicate) {
        makeToastFaile('Sản phẩm trùng!');
        return;
      }

      this.addComboProduct(comboProduct);
      this.hideComboProductModal();
    },

    // Product options methods
    addNewOption() {
      if (this.$refs.productOptions) {
        this.$refs.productOptions.addNewOption();
      }
    },

    updateProductOptions(options) {
      this.setProductOptions(options);
    },

    // Save product
    async saveProduct() {
      if (!this.ensureCanCreateProduct()) {
        return;
      }
      this.$v.form.$touch();
      const isValid = !this.$v.form.$invalid;

      if (!isValid) {
        return;
      }

      // Check required fields
      if (!this.$store.state.product.selectedCategoryId) {
        makeToastFaile('Vui lòng chọn danh mục sản phẩm');
        return;
      }

      // For combo products, check if sum matches selling price
      if (this.productInfo.selectedType === 3) {
        const sumComboPrice = await this.calculateComboSum();
        const sellingPrice = parseFloat(
          this.productInfo.salePrice.replace(/,/g, ''),
        );

        if (sumComboPrice !== sellingPrice) {
          makeToastFaile('Giá bán lẻ không bằng tổng giá sản phẩm combo!');
          return;
        }
      }

      try {
        const result = await this.saveProductAction({
          form: this.form,
          uuid: this.$uuid.v4,
        });

        if (result.status === 1) {
          makeToastSuccess(result.message);
          setTimeout(() => {
            this.$router.push({ name: 'list-products' });
          }, TIME_TRIGGER);
        } else {
          makeToastFaile(result.message);
        }
      } catch (error) {
        console.error('Network/Server error:', error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Có lỗi kết nối xảy ra khi lưu sản phẩm';
        makeToastFaile(errorMessage);
      }
    },

    debounceCreateProduct: debounce(function () {
      this.saveProduct();
    }, TIME_TRIGGER),
  },
};
</script>

<style lang="scss">
@import '@/assets/sass/pages/wizard/wizard-4.scss';

.add-product {
  .input-group-append {
    cursor: pointer;

    &:hover i {
      color: #3699ff;
    }
  }

  .icon:hover {
    background-color: #90c6fc;
  }

  .card-header-tabs {
    margin-right: -21px !important;
    margin-left: -21px !important;
  }

  .full-width .nav-tabs .nav-item {
    margin-bottom: -1px;
    flex-grow: 1 !important;
    text-align: center !important;
  }

  input::-webkit-calendar-picker-indicator {
    display: none;
  }

  .label-header-form {
    font-size: 16px;
  }

  .form-group label {
    font-weight: 500;
  }

  // UI Feedback
  .form-group {
    margin-bottom: 0.75rem;
  }
  .form-group label {
    margin-bottom: 0.25rem;
  }
  .p {
    margin-bottom: 0.25rem;
  }

  .lable-sp {
    font-weight: 500;
  }

  .multi-select-wrapper,
  .auto-autosuggest-wrap-component {
    flex: 1 1 auto;
  }

  .border-grey {
    border-color: #e5eaee !important;
  }

  .overlay-spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
}
</style>
